import { useEffect, useRef, useCallback } from "react";

type ClickAwayHandler = (ev: MouseEvent | TouchEvent | PointerEvent) => void;

function pathIncludes(el: Element | null, ev: Event) {
  if (!el) return false;
  const anyEv = ev as any;
  const path: EventTarget[] | undefined = anyEv.composedPath?.();
  if (path && Array.isArray(path)) {
    return path.includes(el);
  }
  const target = ev.target as Node | null;
  return !!target && el.contains(target);
}

function pathIncludesAnySelector(ev: Event, selectors?: string[]) {
  if (!selectors || selectors.length === 0) return false;
  const anyEv = ev as any;
  const path: EventTarget[] | undefined = anyEv.composedPath?.();
  const elements: Element[] = (path?.filter((n) => n instanceof Element) as Element[]) ?? [];
  return elements.some((el) => selectors.some((sel) => typeof (el as any).matches === "function" && el.matches(sel)));
}

export function useClickAway<T extends HTMLElement>(
  ref: React.RefObject<T>,
  handler: ClickAwayHandler,
  options?: {
    disableReactTree?: boolean;
    triggerOn?: "pointerup" | "click";
    reactTreeRef?: React.MutableRefObject<boolean | null>;
    onEscape?: (ev: KeyboardEvent) => void;
    insideSelectors?: string[];
  }
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const triggerOn = options?.triggerOn ?? "pointerup";

  const startedInsideRef = useRef(false);

  const onPointerDown = useCallback(
    (ev: PointerEvent) => {
      startedInsideRef.current =
        pathIncludes(ref.current as unknown as Element, ev) || pathIncludesAnySelector(ev, options?.insideSelectors);
      // reset reactTreeRef marker at the start of a new interaction
      if (options?.reactTreeRef) options.reactTreeRef.current = null;
    },
    [ref, options?.reactTreeRef, options?.insideSelectors]
  );

  const onTrigger = useCallback(
    (ev: Event) => {
      const endedInside =
        pathIncludes(ref.current as unknown as Element, ev) || pathIncludesAnySelector(ev, options?.insideSelectors);
      const isReactTreeEvent = options?.reactTreeRef?.current === true;

      if (!startedInsideRef.current && !endedInside) {
        if (!(isReactTreeEvent && options?.disableReactTree !== true)) {
          handlerRef.current(ev as any);
        }
      }

      startedInsideRef.current = false;
      if (options?.reactTreeRef) options.reactTreeRef.current = null;
    },
    [ref, options?.reactTreeRef, options?.disableReactTree, options?.insideSelectors]
  );

  useEffect(() => {
    const downOpts = { capture: true } as AddEventListenerOptions;
    const upOpts = { capture: true } as AddEventListenerOptions;

    document.addEventListener("pointerdown", onPointerDown, downOpts);
    document.addEventListener(triggerOn, onTrigger, upOpts);

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && options?.onEscape) {
        options.onEscape(ev);
      }
    };

    if (options?.onEscape) {
      document.addEventListener("keydown", onKeyDown, upOpts);
    }

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, downOpts);
      document.removeEventListener(triggerOn, onTrigger, upOpts);
      if (options?.onEscape) {
        document.removeEventListener("keydown", onKeyDown, upOpts);
      }
    };
  }, [onPointerDown, onTrigger, triggerOn, options?.onEscape]);
}
